import { FormEvent, useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import {
  AggregateDomain,
  AggregateRollupIntegrityReport,
  ApiError,
  api
} from "../../lib/api.js";
import EmptyState from "../../components/EmptyState.js";
import Drawer from "../../components/Drawer.js";
import { useTableState } from "../../lib/useTableState.js";
import TableToolbar from "../../components/TableToolbar.js";

const DOMAIN_TYPES: AggregateDomain["domainType"][] = [
  "dataset-aligned",
  "data-element-group",
  "reporting",
  "publication",
  "hybrid-tracker-to-aggregate",
  "submitted-aggregate-live-rollup"
];

const SOURCE_CLASSES: AggregateDomain["sourceClasses"][number][] = [
  "tracker",
  "warehouse",
  "submitted-aggregate"
];

type Draft = {
  id: string;
  name: string;
  description: string;
  domainType: AggregateDomain["domainType"];
  sourceClassesText: string; // comma-separated
  active: boolean;
  liveRollupEnabled: boolean;
  publicationProfilesText: string;
  dependencyIdsText: string;
  sourceBindingsJson: string;
  targetBindingsJson: string;
  rulesJson: string;
  validationRulesJson: string;
  metricBindingsJson: string;
};

function emptyDraft(): Draft {
  return {
    id: "",
    name: "",
    description: "",
    domainType: "dataset-aligned",
    sourceClassesText: "submitted-aggregate",
    active: false,
    liveRollupEnabled: false,
    publicationProfilesText: "",
    dependencyIdsText: "",
    sourceBindingsJson: "[]",
    targetBindingsJson: "[]",
    rulesJson: "[]",
    validationRulesJson: "[]",
    metricBindingsJson: "[]"
  };
}

function domainToDraft(d: AggregateDomain): Draft {
  return {
    id: d.id,
    name: d.name,
    description: d.description,
    domainType: d.domainType,
    sourceClassesText: d.sourceClasses.join(","),
    active: d.active,
    liveRollupEnabled: d.liveRollupEnabled,
    publicationProfilesText: d.publicationProfiles.join(","),
    dependencyIdsText: d.dependencyIds.join(","),
    sourceBindingsJson: JSON.stringify(d.sourceBindings, null, 2),
    targetBindingsJson: JSON.stringify(d.targetBindings, null, 2),
    rulesJson: JSON.stringify(d.rules, null, 2),
    validationRulesJson: JSON.stringify(d.validationRules, null, 2),
    metricBindingsJson: JSON.stringify(d.metricBindings, null, 2)
  };
}

function draftToDomain(draft: Draft, base: AggregateDomain | null): AggregateDomain {
  const now = new Date().toISOString();
  const splitCsv = (text: string): string[] =>
    text.split(",").map((x) => x.trim()).filter((x) => x.length > 0);
  const parseJson = <T,>(text: string, label: string): T => {
    try {
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) throw new Error(`${label} must be a JSON array`);
      return parsed as T;
    } catch (e) {
      throw new Error(`${label}: ${e instanceof Error ? e.message : String(e)}`);
    }
  };
  const sourceClasses = splitCsv(draft.sourceClassesText) as AggregateDomain["sourceClasses"];
  for (const sc of sourceClasses) {
    if (!SOURCE_CLASSES.includes(sc)) throw new Error(`Unknown source class: ${sc}`);
  }
  return {
    id: draft.id.trim(),
    name: draft.name.trim(),
    description: draft.description.trim(),
    domainType: draft.domainType,
    sourceClasses,
    active: draft.active,
    archived: base?.archived ?? false,
    liveRollupEnabled: draft.liveRollupEnabled,
    version: base?.version ?? 1,
    sourceBindings: parseJson(draft.sourceBindingsJson, "Source bindings"),
    targetBindings: parseJson(draft.targetBindingsJson, "Target bindings"),
    rules: parseJson(draft.rulesJson, "Rules"),
    validationRules: parseJson(draft.validationRulesJson, "Validation rules"),
    metricBindings: parseJson(draft.metricBindingsJson, "Metric bindings"),
    publicationProfiles: splitCsv(draft.publicationProfilesText),
    dependencyIds: splitCsv(draft.dependencyIdsText),
    createdAt: base?.createdAt ?? now,
    updatedAt: now
  };
}

export default function AdminAggregateDomains() {
  const [domains, setDomains] = useState<AggregateDomain[]>([]);
  const [versions, setVersions] = useState<Record<string, Array<{ version: number; createdAt: string; definitionSummary: string }>>>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [editing, setEditing] = useState<AggregateDomain | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState<"all" | "active" | "archived" | "draft">("all");

  const refresh = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.aggregateDomains();
      setDomains(r.domains ?? []);
      const grouped: typeof versions = {};
      for (const v of r.versions ?? []) {
        if (!grouped[v.domainId]) grouped[v.domainId] = [];
        grouped[v.domainId].push({ version: v.version, createdAt: v.createdAt, definitionSummary: v.definitionSummary });
      }
      setVersions(grouped);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, []);

  const filtered = useMemo(() => {
    return domains.filter((d) => {
      if (filter === "active") return d.active && !d.archived;
      if (filter === "archived") return d.archived;
      if (filter === "draft") return !d.active && !d.archived;
      return true;
    });
  }, [domains, filter]);

  const t = useTableState(filtered, {
    searchFields: ["id", "name", "domainType", "sourceClasses"],
    initialPageSize: 25
  });

  const openEditor = (d: AggregateDomain | null) => {
    setEditing(d);
    setCreating(d == null);
    setDraft(d ? domainToDraft(d) : emptyDraft());
    setErr(null);
  };

  const closeEditor = () => {
    setEditing(null);
    setCreating(false);
    setDraft(emptyDraft());
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      const payload = draftToDomain(draft, editing);
      if (!payload.id) throw new Error("Domain id is required.");
      if (!payload.name) throw new Error("Domain name is required.");
      const r = await api.upsertAggregateDomain(payload);
      setBanner(`Saved aggregate domain ${r.domain.id} (v${r.domain.version}).`);
      closeEditor();
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const onActivate = async (d: AggregateDomain, active: boolean) => {
    try {
      const r = await api.activateAggregateDomain(d.id, active);
      setBanner(`${active ? "Activated" : "Deactivated"} ${r.domain.id}.`);
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    }
  };

  const onArchive = async (d: AggregateDomain) => {
    if (!confirm(`Archive aggregate domain "${d.name}"?`)) return;
    try {
      await api.archiveAggregateDomain(d.id);
      setBanner(`Archived ${d.id}.`);
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    }
  };

  const onClone = async (d: AggregateDomain) => {
    const cloneId = prompt(`Clone "${d.id}" as new id:`, `${d.id}-clone`);
    if (!cloneId) return;
    try {
      const r = await api.cloneAggregateDomain(d.id, cloneId);
      setBanner(`Cloned to ${r.domain.id}.`);
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    }
  };

  const [integrityReport, setIntegrityReport] = useState<AggregateRollupIntegrityReport | null>(null);
  const [integrityDomainId, setIntegrityDomainId] = useState<string | null>(null);

  const onRestate = async (d: AggregateDomain, mode: "incremental" | "full-rebuild") => {
    if (mode === "full-rebuild" && !confirm(`Full-rebuild rollups for "${d.name}"? This drops and recomputes every rollup fact in this domain.`)) return;
    try {
      const r = await api.restateAggregateDomain(d.id, mode);
      setBanner(`Restate (${mode}) completed for ${d.id}: ${r.run.producedRollupCount} rollups, ${r.run.affectedFactCount} facts.`);
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    }
  };

  const onCheckIntegrity = async (d: AggregateDomain) => {
    try {
      const r = await api.validateAggregateRollupIntegrity(d.id);
      setIntegrityReport(r.report);
      setIntegrityDomainId(d.id);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Aggregate Domain Studio</h1>
          <p>Author and manage aggregate domain definitions: source bindings, rollup rules, validation rules, and publication targets.</p>
        </div>
        <div className="actions">
          <button className="btn" type="button" onClick={() => void refresh()} disabled={loading}>Refresh</button>
          <button className="btn btn-primary" type="button" onClick={() => openEditor(null)}>New domain</button>
        </div>
      </header>

      {err && <div className="alert alert-error">{err}</div>}
      {banner && <div className="alert alert-ok">{banner}</div>}

      <div className="tabs" style={{ display: "flex", gap: 4, flexWrap: "wrap", borderBottom: "1px solid #e5e7eb", marginBottom: 12 }}>
        {(["all", "active", "draft", "archived"] as const).map((k) => (
          <button
            key={k}
            type="button"
            className={`btn ${filter === k ? "btn-primary" : ""}`}
            style={{ borderRadius: "6px 6px 0 0" }}
            onClick={() => setFilter(k)}
          >
            {k[0].toUpperCase() + k.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <SkeletonTable rows={5} columns={4} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="🧩"
          title="No aggregate domains"
          description="Create your first aggregate domain to define how source facts roll up into reportable, publishable aggregates."
        />
      ) : (
        <div className="table-scroll">
          <TableToolbar
            hideExport
            search={t.search}
            setSearch={t.setSearch}
            page={t.page}
            setPage={t.setPage}
            pageSize={t.pageSize}
            setPageSize={t.setPageSize}
            totalPages={t.totalPages}
            totalRows={t.totalRows}
            searchPlaceholder="Search aggregate domains…"
          />
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Type</th>
                <th>Sources</th>
                <th>Status</th>
                <th>Version</th>
                <th>Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {t.paged.map((d) => (
                <tr key={d.id} style={{ cursor: "pointer" }} onClick={() => openEditor(d)}>
                  <td><code>{d.id}</code></td>
                  <td>{d.name}</td>
                  <td className="soft">{d.domainType}</td>
                  <td className="soft">{d.sourceClasses.join(", ")}</td>
                  <td>
                    {d.archived ? <span className="pill pill-muted">Archived</span> :
                     d.active ? <span className="pill pill-ok">Active</span> :
                     <span className="pill pill-info">Draft</span>}
                    {d.liveRollupEnabled ? <span className="pill pill-info" style={{ marginLeft: 4 }}>Live</span> : null}
                  </td>
                  <td className="soft">v{d.version}</td>
                  <td className="soft">{new Date(d.updatedAt).toLocaleString()}</td>
                  <td>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} onClick={(e) => e.stopPropagation()}>
                      <button type="button" className="btn btn-sm" onClick={() => openEditor(d)}>Edit</button>
                      {!d.archived && (
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => void onActivate(d, !d.active)}
                        >
                          {d.active ? "Deactivate" : "Activate"}
                        </button>
                      )}
                      <button type="button" className="btn btn-sm" onClick={() => void onClone(d)}>Clone</button>
                      {!d.archived && (
                        <>
                          <button type="button" className="btn btn-sm" onClick={() => void onRestate(d, "incremental")}>Restate (inc.)</button>
                          <button type="button" className="btn btn-sm" onClick={() => void onRestate(d, "full-rebuild")}>Restate (full)</button>
                        </>
                      )}
                      <button type="button" className="btn btn-sm" onClick={() => void onCheckIntegrity(d)}>Check integrity</button>
                      {!d.archived && (
                        <button type="button" className="btn btn-sm btn-danger" onClick={() => void onArchive(d)}>Archive</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Drawer
        open={editing != null || creating}
        title={creating ? "New aggregate domain" : `Edit ${editing?.name ?? editing?.id ?? ""}`}
        subtitle={editing ? <code>{editing.id} · v{editing.version}</code> : undefined}
        onClose={closeEditor}
        width={720}
        asForm
        onSubmit={onSubmit}
        footer={(
          <>
            <button type="button" className="btn" onClick={closeEditor}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? "Saving…" : creating ? "Create" : "Save"}
            </button>
          </>
        )}
      >
        <div className="field">
          <label>ID</label>
          <input
            type="text"
            required
            value={draft.id}
            disabled={!creating}
            onChange={(e) => setDraft({ ...draft, id: e.target.value })}
            placeholder="agg-immunization-rollup"
          />
        </div>
        <div className="field">
          <label>Name</label>
          <input
            type="text"
            required
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </div>
        <div className="field">
          <label>Description</label>
          <textarea
            rows={2}
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          />
        </div>
        <div className="field">
          <label>Domain type</label>
          <select
            value={draft.domainType}
            onChange={(e) => setDraft({ ...draft, domainType: e.target.value as Draft["domainType"] })}
          >
            {DOMAIN_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Source classes (comma-separated)</label>
          <input
            type="text"
            value={draft.sourceClassesText}
            onChange={(e) => setDraft({ ...draft, sourceClassesText: e.target.value })}
            placeholder="submitted-aggregate,tracker"
          />
          <small className="muted">Allowed: {SOURCE_CLASSES.join(", ")}</small>
        </div>
        <div className="field" style={{ display: "flex", gap: 16 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={draft.active}
              onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
            />
            Active
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={draft.liveRollupEnabled}
              onChange={(e) => setDraft({ ...draft, liveRollupEnabled: e.target.checked })}
            />
            Live rollup
          </label>
        </div>
        <div className="field">
          <label>Publication profiles (comma-separated)</label>
          <input
            type="text"
            value={draft.publicationProfilesText}
            onChange={(e) => setDraft({ ...draft, publicationProfilesText: e.target.value })}
          />
        </div>
        <div className="field">
          <label>Dependency domain IDs (comma-separated)</label>
          <input
            type="text"
            value={draft.dependencyIdsText}
            onChange={(e) => setDraft({ ...draft, dependencyIdsText: e.target.value })}
          />
        </div>

        <h3 style={{ marginTop: 16 }}>Source bindings (JSON)</h3>
        <textarea
          rows={6}
          style={{ fontFamily: "monospace", fontSize: 12, width: "100%" }}
          value={draft.sourceBindingsJson}
          onChange={(e) => setDraft({ ...draft, sourceBindingsJson: e.target.value })}
        />
        <small className="muted">Each entry: {"{ id, sourceClass, dataSetIds?, dataElementIds?, ... }"}</small>

        <h3 style={{ marginTop: 16 }}>Target bindings (JSON)</h3>
        <textarea
          rows={4}
          style={{ fontFamily: "monospace", fontSize: 12, width: "100%" }}
          value={draft.targetBindingsJson}
          onChange={(e) => setDraft({ ...draft, targetBindingsJson: e.target.value })}
        />

        <h3 style={{ marginTop: 16 }}>Rollup rules (JSON)</h3>
        <textarea
          rows={5}
          style={{ fontFamily: "monospace", fontSize: 12, width: "100%" }}
          value={draft.rulesJson}
          onChange={(e) => setDraft({ ...draft, rulesJson: e.target.value })}
        />
        <small className="muted">Rule types: simple-sum, period-rollup, org-rollup, category-grouping, attribute-grouping, weighted, hybrid-comparison</small>

        <h3 style={{ marginTop: 16 }}>Validation rules (JSON)</h3>
        <textarea
          rows={4}
          style={{ fontFamily: "monospace", fontSize: 12, width: "100%" }}
          value={draft.validationRulesJson}
          onChange={(e) => setDraft({ ...draft, validationRulesJson: e.target.value })}
        />

        <h3 style={{ marginTop: 16 }}>Metric bindings (JSON)</h3>
        <textarea
          rows={4}
          style={{ fontFamily: "monospace", fontSize: 12, width: "100%" }}
          value={draft.metricBindingsJson}
          onChange={(e) => setDraft({ ...draft, metricBindingsJson: e.target.value })}
        />

        {/* version history below */}
        {editing && versions[editing.id]?.length ? (
          <>
            <h3 style={{ marginTop: 16 }}>Version history</h3>
            <ul style={{ fontSize: 12 }}>
              {versions[editing.id].map((v) => (
                <li key={v.version}>
                  v{v.version} · <span className="soft">{new Date(v.createdAt).toLocaleString()}</span> · {v.definitionSummary}
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </Drawer>

      <Drawer
        open={integrityReport != null}
        title={`Rollup integrity · ${integrityDomainId ?? ""}`}
        subtitle={integrityReport ? <span className="soft">Checked {new Date(integrityReport.checkedAt).toLocaleString()}</span> : undefined}
        onClose={() => { setIntegrityReport(null); setIntegrityDomainId(null); }}
        width={720}
        footer={(
          <button type="button" className="btn" onClick={() => { setIntegrityReport(null); setIntegrityDomainId(null); }}>Close</button>
        )}
      >
        {integrityReport ? (
          <>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
              <span className="pill pill-info">Rollups: {integrityReport.totalRollupFacts}</span>
              <span className="pill pill-info">Lineage rows: {integrityReport.totalLineageRows}</span>
              <span className="pill pill-ok">Match: {integrityReport.matchCount}</span>
              <span className={`pill ${integrityReport.mismatchCount === 0 ? "pill-muted" : "pill-warn"}`}>
                Mismatch: {integrityReport.mismatchCount}
              </span>
              <span className={`pill ${integrityReport.orphanedLineageCount === 0 ? "pill-muted" : "pill-warn"}`}>
                Orphaned: {integrityReport.orphanedLineageCount}
              </span>
            </div>
            {integrityReport.results.filter((r) => r.status !== "match").length === 0 ? (
              <EmptyState icon="✅" title="All rollups balance" description="Every rollup fact equals the sum of its lineage source values." />
            ) : (
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Rollup fact</th>
                      <th style={{ textAlign: "right" }}>Rollup value</th>
                      <th style={{ textAlign: "right" }}>Lineage sum</th>
                      <th style={{ textAlign: "right" }}>Δ</th>
                      <th style={{ textAlign: "right" }}>Lineage rows</th>
                    </tr>
                  </thead>
                  <tbody>
                    {integrityReport.results
                      .filter((r) => r.status !== "match")
                      .map((r) => (
                        <tr key={r.rollupFactId}>
                          <td>
                            <span className={`pill ${r.status === "mismatch" ? "pill-warn" : "pill-muted"}`}>{r.status}</span>
                          </td>
                          <td><code>{r.rollupFactId}</code></td>
                          <td style={{ textAlign: "right" }}>{r.rollupValue}</td>
                          <td style={{ textAlign: "right" }}>{r.lineageSum}</td>
                          <td style={{ textAlign: "right" }}>{r.difference}</td>
                          <td style={{ textAlign: "right" }}>{r.lineageRowCount}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="muted" style={{ marginTop: 12, fontSize: 12 }}>
              To repair a corrupt domain, run "Restate (full)" — it drops all rollup facts and lineage and recomputes them from source facts.
            </p>
          </>
        ) : null}
      </Drawer>
    </div>
  );
}
